import { UseQueryResult } from '@tanstack/react-query'
import { useAuthFetchWithResponse } from '@e3dc-react/shell/Hooks/AuthFetch'
import { isBefore } from 'date-fns'
import { getFiles } from '../api'
import { File, FileWithLatestStatus } from '../models'
import { FileType } from '../types/files'
import { usePortalQuery } from 'src/Libs/api'

type DataReturnType = [FileWithLatestStatus[], boolean, () => Promise<UseQueryResult>]

interface ParamsData {
  installationId?: number
  deviceLinkId: number
  filter?: string
}

export const modifyFilesToGetLatestStatus = (data: File[]): FileWithLatestStatus[] => {
  const latestIndexesByType = {} as Record<FileType, number>

  data.forEach(({ type, createdAt }, currentElementIndex) => {
    const latestElementByType = data?.[latestIndexesByType?.[type]]
    const isCurrentOlder = isBefore(new Date(latestElementByType?.createdAt), new Date(createdAt))

    if (isCurrentOlder || !latestElementByType) {
      latestIndexesByType[type] = currentElementIndex
    }
  })

  const resultData = data.map((elem, index) => ({
    ...elem,
    isLatestVersion: latestIndexesByType[elem.type] === index,
  }))

  return resultData
}

const useFiles = ({ deviceLinkId, installationId, filter }: ParamsData): DataReturnType => {
  const authFetch = useAuthFetchWithResponse()

  const getFilesQueryFn = async (): Promise<File[] | null> => {
    const { error, hasErrors, value } = await getFiles({
      installationId,
      deviceLinkId,
      authFetch,
      filter,
    })

    if (hasErrors) {
      throw error || new Error('An unexpected error occurred while fetching installation statement files')
    }

    return value
  }

  const { data, isLoading, refetch } = usePortalQuery({
    queryKey: ['installations', installationId, 'device-links', deviceLinkId, 'statements', 'files', filter],
    queryFn: getFilesQueryFn,
    enabled: !!installationId && !!deviceLinkId,
  })

  const dataResult = modifyFilesToGetLatestStatus(data || [])

  return [dataResult, isLoading, refetch]
}

export default useFiles
